import { i18n } from "@/i18n/index";
import FroalaEditor from "froala-editor";
import icons from "@/components/DocumentEditor/Froala/FroalaIcons";
import { latinCharSet } from "@/components/DocumentEditor/Froala/SpecialCharacters";
import "@/components/DocumentEditor/Froala/DateFormat/DateFormatPlugin";

FroalaEditor.DefineIconTemplate("customSVG", '<svg class="fr-svg" focusable="false" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">[PATH]</svg>');
FroalaEditor.DefineIconTemplate(
    "textSVG",
    '<div style="display: flex; align-items: center;">'
        + '<svg class="fr-svg" focusable="false" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">[PATH]</svg>'
        + '<span class="button-text" style="display: inline-block;">[NAME]</span></div>'
);

FroalaEditor.DefineIcon("insertPageBreak", { template: "customSVG", PATH: icons.pageBreakIcon });
FroalaEditor.DefineIcon("pastePlainText", { template: "customSVG", PATH: icons.plainTextIcon });
FroalaEditor.DefineIcon("toggleBlocks", { template: "customSVG", PATH: icons.blocksIcon });
FroalaEditor.DefineIcon("directionRTL", { template: "customSVG", PATH: icons.rtlIcon });
FroalaEditor.DefineIcon("directionLTR", { template: "customSVG", PATH: icons.ltrIcon });
FroalaEditor.DefineIcon("lineBeforeTable", { template: "customSVG", PATH: icons.lineBefore });
FroalaEditor.DefineIcon("lineAfterTable", { template: "customSVG", PATH: icons.lineAfter });
FroalaEditor.DefineIcon("transposeTable", { template: "customSVG", PATH: icons.transpose });
FroalaEditor.DefineIcon("reorder", { template: "customSVG", PATH: icons.reorder });
FroalaEditor.RegisterCommand("insertPageBreak", {
    title: i18n.t("froala.insertPageBreakTooltip"),
    focus: true,
    undo: true,
    refreshAfterCallback: true,
    callback() {
        this.html.insert('<p style="page-break-before:always;" class="fr-deletable page-break" contenteditable="false"><!-- pagebreak --></p>&nbsp;');
    }
});

FroalaEditor.RegisterCommand("pastePlainText", {
    title: i18n.t("froala.pastePlainTextTooltip"),
    focus: true,
    undo: true,
    refreshAfterCallback: true,
    refresh($btn) {
        if (this.opts.pastePlain) {
            $btn.addClass("fr-active");
        } else {
            $btn.removeClass("fr-active");
        }
    },
    callback() {
        this.opts.pastePlain = !this.opts.pastePlain;
        if (this.opts.pastePlain) {
            this.opts.pasteDeniedTags = [
                "table",
                "tbody",
                "td",
                "tfoot",
                "th",
                "thead",
                "tr",
                "tt",
                "img",
                "a",
                "script",
                "style",
                "div",
                "ul",
                "ol",
                "li",
                "section",
                "object",
                "article"
            ];
            this.opts.pasteDeniedAttrs = ["style", "class", "id", "contenteditable"];
        } else {
            this.opts.pasteDeniedTags = ["img", "script", "iframe"];
            this.opts.pasteDeniedAttrs = [];
        }
    }
});
FroalaEditor.RegisterCommand("toggleBlocks", {
    title: i18n.t("froala.toggleBlocksTooltip"),
    focus: true,
    undo: true,
    refreshAfterCallback: true,
    refresh($btn) {
        if (this.opts.showBlocks) {
            $btn.addClass("fr-active");
        } else {
            $btn.removeClass("fr-active");
        }
    },
    callback() {
        this.opts.showBlocks = this.vueInstance.toggleShowBlocks();
    }
});

function changeDirection(dir, align, bidi) {
    // Wrap block tags.
    this.selection.save();
    this.html.wrap(true, true, true, true);
    this.selection.restore();

    // Get blocks.
    const elements = this.selection.blocks();
    // Save selection to restore it later.
    this.selection.save();

    for (let i = 0; i < elements.length; i++) {
        let element = elements[i];
        if (this.$(element).is(".conditional-block, .conditional-inner")) {
            element = this.$(element)
                .parentsUntil(this.$el, "p, th, td, table, div:not(.conditional-inner)")
                .first();
        }
        if (element !== this.el) {
            this.$(element)
                .css("direction", dir)
                .css("unicode-bidi", bidi)
                .css("text-align", align)
                .removeClass("fr-temp-div");
        }
    }

    // Unwrap temp divs.
    this.html.unwrap();

    // Restore selection.
    this.selection.restore();
}

FroalaEditor.RegisterCommand("directionRTL", {
    title: i18n.t("froala.directionRTLTooltip"),
    focus: true,
    undo: true,
    refreshAfterCallback: true,
    refresh($btn) {
        if (this.$(this.selection.element()).css("direction") === "rtl") {
            $btn.addClass("fr-active");
        } else {
            $btn.removeClass("fr-active");
        }
    },
    callback() {
        changeDirection.apply(this, ["rtl", "right", "bidi-override"]);
    }
});
FroalaEditor.RegisterCommand("directionLTR", {
    title: i18n.t("froala.directionLTRTooltip"),
    focus: true,
    undo: true,
    refreshAfterCallback: true,
    refresh($btn) {
        if (this.$(this.selection.element()).css("direction") === "rtl") {
            $btn.removeClass("fr-active");
        } else {
            $btn.addClass("fr-active");
        }
    },
    callback() {
        changeDirection.apply(this, ["ltr", "left", "normal"]);
    }
});

function insertLine(before) {
    const el = this.$el.find(".fr-selected-cell").closest("table");
    const line = "<p><br/></p>";
    this.popups.hideAll();
    if (before) {
        el.before(line);
    } else {
        el.after(line);
    }
}

FroalaEditor.RegisterCommand("lineBeforeTable", {
    title: i18n.t("froala.insertLineBefore"),
    undo: true,
    focus: false,
    callback() {
        insertLine.apply(this, [true]);
    }
});

FroalaEditor.RegisterCommand("lineAfterTable", {
    title: i18n.t("froala.insertLineAfter"),
    undo: true,
    focus: false,
    callback() {
        insertLine.apply(this, [false]);
    }
});

function transposeTable() {
    const table = this.$el.find(".fr-selected-cell").closest("table")[0];
    const originalRows = table.rows;
    const numRows = originalRows.length;
    const numCols = originalRows[0].cells.length;
    const transposedTable = document.createElement("table");
    Array.from(table.attributes).forEach(attribute => {
        transposedTable.setAttribute(attribute.nodeName, attribute.nodeValue);
    });
    for (let col = 0; col < numCols; col++) {
        const newRow = transposedTable.insertRow();
        for (let row = 0; row < numRows; row++) {
            const originalCell = originalRows[row].cells[col];
            if (originalCell) {
                originalCell.style.width = 0;
                const newCell = newRow.insertCell();
                newCell.outerHTML = originalCell.outerHTML;
            }
        }
    }
    table.parentElement.replaceChild(transposedTable, table);
    transposedTable.style.cssText = table.style.cssText;
    transposedTable.className = table.className;
}

FroalaEditor.RegisterCommand("transposeTable", {
    title: i18n.t("froala.transpose"),
    undo: true,
    focus: false,
    refresh($btn) {
        const table = this.$el.find(".fr-selected-cell").closest("table");
        // eslint-disable-next-line max-len
        if (table[0].querySelectorAll(":scope > tbody > tr > td[rowspan], :scope > tbody > tr > td[colspan], :scope > tbody > tr > th[rowspan], :scope > tbody > tr > th[colspan],  :scope > thead > tr > th[colspan],  :scope > thead > tr > th[rowspan]").length || table[0].classList.contains("froala-table")) {
            $btn.addClass("fr-disabled");
            $btn[0].title = i18n.t("froala.transposeDisabled");
        } else {
            $btn.removeClass("fr-disabled");
            $btn[0].title = i18n.t("froala.transpose");
        }
    },
    callback() {
        transposeTable.apply(this);
    }
});

function moveColumn(rows, to, from) {
    rows.forEach(elem => {
        const nodev = elem.children[to];
        elem.removeChild(nodev);
        elem.insertBefore(nodev, elem.children[from]);
    });
}

FroalaEditor.RegisterCommand("reorder", {
    title: i18n.t("froala.reorder"),
    type: "dropdown",
    undo: true,
    focus: true,
    options: {
        moveUp: i18n.t("froala.moveUp"),
        moveDown: i18n.t("froala.moveDown"),
        moveLeft: i18n.t("froala.moveLeft"),
        moveRight: i18n.t("froala.moveRight")
    },
    refreshOnShow() {
        const table = this.$el.find(".fr-selected-cell").closest("table");
        document.querySelector("[data-param1='moveLeft']").classList.remove("fr-disabled");
        document.querySelector("[data-param1='moveRight']").classList.remove("fr-disabled");
        document.querySelector("[data-param1='moveUp']").classList.remove("fr-disabled");
        document.querySelector("[data-param1='moveDown']").classList.remove("fr-disabled");
        document.querySelector("[data-param1='moveUp']").title = i18n.t("froala.moveUp");
        document.querySelector("[data-param1='moveDown']").title = i18n.t("froala.moveDown");
        document.querySelector("[data-param1='moveLeft']").title = i18n.t("froala.moveLeft");
        document.querySelector("[data-param1='moveRight']").title = i18n.t("froala.moveRight");
        if (table[0].querySelector(":scope > tbody > tr > td[rowspan], :scope > thead > tr > th[rowspan]")) {
            document.querySelector("[data-param1='moveUp']").classList.add("fr-disabled");
            document.querySelector("[data-param1='moveDown']").classList.add("fr-disabled");
            document.querySelector("[data-param1='moveLeft']").classList.add("fr-disabled");
            document.querySelector("[data-param1='moveRight']").classList.add("fr-disabled");
            document.querySelector("[data-param1='moveUp']").title = i18n.t("froala.reorderDisabled");
            document.querySelector("[data-param1='moveDown']").title = i18n.t("froala.reorderDisabled");
            document.querySelector("[data-param1='moveLeft']").title = i18n.t("froala.reorderDisabled");
            document.querySelector("[data-param1='moveRight']").title = i18n.t("froala.reorderDisabled");
        // eslint-disable-next-line max-len
        } else if (table[0].querySelectorAll(":scope > tbody > tr > td[colspan], :scope > tbody > tr > th[colspan], :scope > thead > tr > th[colspan]").length) {
            document.querySelector("[data-param1='moveLeft']").classList.add("fr-disabled");
            document.querySelector("[data-param1='moveRight']").classList.add("fr-disabled");
            document.querySelector("[data-param1='moveLeft']").title = i18n.t("froala.reorderDisabled");
            document.querySelector("[data-param1='moveRight']").title = i18n.t("froala.reorderDisabled");
        }
    },
    callback(cmd, val) {
        const row = this.$el.find(".fr-selected-cell").closest("tr");
        const rowIndex = [...row[0].parentElement.children].indexOf(row[0]);
        const table = this.$el.find(".fr-selected-cell").closest("table");
        const rows = table[0].querySelectorAll(":scope > tbody > tr, :scope > thead > tr");
        const selectedTd = this.$el.find(".fr-selected-cell")[0];
        const columnIndex = [...selectedTd.parentElement.children].indexOf(selectedTd);
        switch (val) {
            case "moveUp":
                if (rowIndex > 0) {
                    row.prev().before(row);
                }
                break;
            case "moveDown":
                if (rowIndex < row[0].parentElement.children.length - 1) {
                    row.next().after(row);
                }
                break;
            case "moveRight":
                moveColumn.apply(this, [rows, columnIndex, columnIndex + 1]);
                break;
            case "moveLeft":
                if (columnIndex > 0) {
                    moveColumn.apply(this, [rows, columnIndex, columnIndex - 1]);
                }
                break;
            default:
        }
    }
});

FroalaEditor.RegisterShortcut(70, "fullscreen", false, "F", true);
export const config = {
    language: i18n.locale,
    immediateVueModelUpdate: true,
    pasteDeniedTags: ["img", "script", "iframe"],
    htmlRemoveTags: ["script"],
    pasteDeniedAttrs: [],
    wordDeniedTags: [],
    charCounterMax: -1,
    specialCharactersSets: latinCharSet,
    key: "lSA3D-17C1E1B1A1G2B1rXYb1VPUGRHYZNRJd1JVOOb1HAc1zG2B1A2B2D6B1B2C4F1C3==",
    toolbarInline: false,
    imageEditButtons: ["imageRemove", "|", "imageLink", "linkOpen", "linkEdit", "linkRemove", "-", "imageDisplay", "imageAlt", "imageSize"],
    imageInsertButtons: ["imageBack", "|", "imageUpload", "imageByURL"],
    imageMultipleStyles: false,
    imagePaste: true,
    imageOutputSize: false,
    imageDefaultWidth: 0,
    inlineClasses: {
        "fr-class-code": "Code",
        "fr-class-highlighted": "Highlighted",
        "fr-class-transparency": "Transparent"
    },
    imageTextNear: true,
    imageUpload: true,
    imageUploadMethod: "POST",
    imageResize: true,
    inlineStyles: {
        "custom 1": "font-size: 20px; color: #696969;",
        "custom 2": "font-size: 12px; color: #422d9a;"
    },
    quickInsertEnabled: false,
    toolbarButtons: {
        moreText: {
            buttons: [
                "fontFamily",
                "|",
                "fontSize",
                "|",
                "bold",
                "italic",
                "underline",
                "textColor",
                "paragraphFormat",
                "paragraphStyle",
                "strikeThrough",
                "subscript",
                "superscript",
                "backgroundColor",
                "clearFormatting",
                "|"
            ],
            buttonsVisible: 6,
            align: "left"
        },
        moreParagraph: {
            buttons: ["alignLeft", "alignCenter", "alignRight", "alignJustify", "lineHeight", "formatUL", "formatOL", "indent", "outdent", "quote", "|"],
            buttonsVisible: 5,
            align: "left"
        },
        moreRich: {
            buttons: ["insertTable", "insertLink", "insertImage", "specialCharacters", "insertHR", "|"],
            buttonsVisible: 4,
            align: "left"
        },
        moreMisc: {
            buttons: [
                "undo",
                "redo",
                "insertPageBreak",
                "toggleBlocks",
                "pastePlainText",
                "directionLTR",
                "directionRTL",
                "fullscreen",
                "spellChecker",
                "selectAll",
                "help"
            ],
            align: "left",
            buttonsVisible: 10
        }
    },
    fontFamily: {
        "EncodeSans-ExtraLight": "EncodeSans ExtraLight",
        "EncodeSans-Light": "EncodeSans Light",
        "EncodeSans-Medium": "EncodeSans Medium",
        "EncodeSansCondensed-Thin": "EncodeSansCondensed Thin",
        "EncodeSansCondensed-Light": "EncodeSansCondensed Light",
        "EncodeSansCondensed-Bold": "EncodeSansCondensed Bold",
        Roboto: "Roboto",
        RobotoCondensed: "Roboto Condensed",
        RobotoMono: "Roboto Mono",
        RobotoSlab: "Roboto Slab",
        Lato: "Lato",
        Merriweather: "Merriweather",
        Montserrat: "Montserrat",
        NotoSans: "Noto Sans",
        NotoSerif: "Noto Serif",
        OpenSans: "Open Sans",
        Oswald: "Oswald",
        Raleway: "Raleway"
    },
    fontFamilySelection: true,
    fontSizeSelection: true,
    paragraphFormatSelection: true,
    spellcheck: false,
    direction: "ltr",
    tooltips: true,
    width: "auto",
    tabSpaces: 4,
    typingTimer: 50,
    tableEditButtons: [
        "transposeTable",
        "tableHeader",
        "tableRemove",
        "tableRows",
        "tableColumns",
        "tableStyle",
        "lineBeforeTable",
        "-",
        "tableCells",
        "tableCellBackground",
        "tableCellVerticalAlign",
        "tableCellHorizontalAlign",
        "tableCellStyle",
        "reorder",
        "lineAfterTable"
    ],
    tableStyles: {
        "fr-default-borders": "Default Borders",
        "fr-dashed-borders": "Dashed Borders",
        "fr-no-cell-spacing": "Zero Cell Spacing",
        "fr-outside-border": "Outside Border",
        "fr-alternate-rows": "Alternate Rows",
        "fr-collapsed-borders": "Shared Inner Borders"
    },
    tableCellStyles: {
        "fr-highlighted": "Highlighted",
        "fr-thick": "Thick",
        "fr-solid": "Solid Border"
    },
    helpSets: [
        {
            title: "Inline Editor",
            commands: [{ val: "OSkeyE", desc: "Show the editor" }]
        },
        {
            title: "Common actions",
            commands: [
                { val: "OSkeyC", desc: "Copy" },
                { val: "OSkeyX", desc: "Cut" },
                { val: "OSkeyV", desc: "Paste" },
                { val: "OSkeyZ", desc: "Undo" },
                { val: "OSkeyShift+Z", desc: "Redo" },
                { val: "OSkeyK", desc: "Insert Link" },
                { val: "OSkeyP", desc: "Insert Image" }
            ]
        },
        {
            title: "Basic Formatting",
            commands: [
                { val: "OSkeyA", desc: "Select All" },
                { val: "OSkeyB", desc: "Bold" },
                { val: "OSkeyI", desc: "Italic" },
                { val: "OSkeyU", desc: "Underline" },
                { val: "OSkeyS", desc: "Strikethrough" },
                { val: "OSkey]", desc: "Increase Indent" },
                { val: "OSkey[", desc: "Decrease Indent" }
            ]
        },
        {
            title: "Quote",
            commands: [
                { val: "OSkey'", desc: "Increase quote level" },
                {
                    val: "OSkeyShift+'",
                    desc: "Decrease quote level"
                }
            ]
        },
        {
            title: "Image / Video",
            commands: [
                { val: "OSkey+", desc: "Resize larger" },
                { val: "OSkey-", desc: "Resize smaller" }
            ]
        },
        {
            title: "Table",
            commands: [
                { val: "Alt+Space", desc: "Select table cell" },
                {
                    val: "Shift+Left/Right arrow",
                    desc: "Extend selection one cell"
                },
                {
                    val: "Shift+Up/Down arrow",
                    desc: "Extend selection one row"
                }
            ]
        },
        {
            title: "Navigation",
            commands: [
                { val: "OSkey/", desc: "Shortcuts" },
                { val: "Alt+F10", desc: "Focus popup / toolbar" },
                {
                    val: "Esc",
                    desc: "Return focus to previous position"
                }
            ]
        }
    ]
};
